import React from 'react';
import { TagColored, Button, ButtonBadged, Text, SparkIcon } from '@spark';
import { summarizeFiltersAsPills, countActiveFilters } from './data/performanceMetricsApi';
import CostConfigPopover from './CostConfigPopover';


/**
 * "Filters Selected: <pills> [Clear All]" left side, optional "Cost
 * configuration" trigger + "Filter Metrics (badge)" button on the right.
 *
 * The Cost configuration popover is rendered here only in comparison
 * mode (multiple environments), where there is no cost banner to host
 * it. In single-environment view the popover lives inside `CostBanner`
 * itself, anchored to the banner's right edge.
 *
 * @param {Object} props
 * @param {import('./data/performanceMetricsApi').PerformanceFilters} props.filters
 * @param {() => void} props.onOpenFilters
 * @param {() => void} props.onClearFilters
 * @param {import('./data/performanceMetricsApi').CostConfig} [props.costConfig]
 * @param {(next: import('./data/performanceMetricsApi').CostConfig) => void} [props.onCostConfigChange]
 * @param {boolean} [props.showCostConfig] When true, render the Cost
 *        configuration trigger to the left of "Filter Metrics".
 *        Caller should set this only in comparison mode.
 * @param {boolean} [props.showActiveFilters] When false, hide the
 *        "Filters Selected" label and pills (comparison mode). Clear All
 *        remains when filters are active.
 */
export default function PerformanceFiltersRow({
  filters,
  onOpenFilters,
  onClearFilters,
  costConfig,
  onCostConfigChange,
  showCostConfig = false,
  showActiveFilters = true,
}) {
  const pills = summarizeFiltersAsPills(filters);
  const count = countActiveFilters(filters);
  const hasFilters = count > 0;
  const showLeftSection = showActiveFilters || hasFilters;

  return (
    <div
      className="fs-unmask"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: showLeftSection ? 'space-between' : 'flex-end',
        gap: 12,
        marginBottom: 16,
      }}
    >
      {showLeftSection && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', minWidth: 0 }}>
          {showActiveFilters && (
            <>
              <Text variant="base-regular" color="secondary" style={{ marginRight: 4 }}>
                Filters Selected:
              </Text>
              {hasFilters ? (
                pills.map((p) => (
                  <TagColored
                    key={p.key}
                    label={p.label}
                    color="grey"
                  />
                ))
              ) : (
                <Text variant="base-regular" color="secondary">No filters selected</Text>
              )}
            </>
          )}
          {hasFilters && (
            <Button
              size="small"
              onClick={onClearFilters}
              variant="secondary"
              style={{ marginLeft: showActiveFilters ? 4 : 0 }}
            >
              Clear All
            </Button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        {showCostConfig && (
          <CostConfigPopover value={costConfig} onChange={onCostConfigChange} />
        )}
        <ButtonBadged
          startIcon={<SparkIcon slug="filter" />}
          onClick={onOpenFilters}
          variant="secondary"
          badgeVisible={count > 0}
          badgeAmount={String(count)}
          badgeColor="info"
        >
          Filter Metrics
        </ButtonBadged>
      </div>
    </div>
  );
}
